/**
 * ORA - AI Leadership Agent
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * Repository: https://github.com/s2cpaul/ora-agent
 * 
 * ============================================================================
 * DESIGN RULES - MAIN APP
 * ============================================================================
 * 1. Desktop screen defaults to DARK MODE on initial load
 * 2. AI Agent modals ALWAYS display in LIGHT MODE (see agent components)
 * 3. Users can toggle between light/dark mode using the theme toggle button
 * 4. Build version: 2.0.1
 * ============================================================================
 */

import { useState } from "react";
import { AnalyticsDashboard } from "./components/AnalyticsDashboard";
import { UnifiedAIAgentPage } from "./components/UnifiedAIAgentPage";
import { ConfigurationPage } from "./components/ConfigurationPage";
import { MissionPage } from "./components/MissionPage";
import { AboutPage } from "./components/AboutPage";
import { AIAgentModal } from "./components/AIAgentModal";
import { AuthProvider } from "@/contexts/AuthContext";
import { NavigationProvider, useNavigation } from "@/contexts/NavigationContext";
import { AgentConnectionProvider } from "@/contexts/AgentConnectionContext";

// ============================================================================
// NOTE: OLD HOME PAGE COMPONENT HAS BEEN COMPLETELY REMOVED AND DETACHED
// All traffic to '/' is now redirected to '/unified-agent' in the AppRouter
// The unified-agent is the ONLY primary interface for the application
// ============================================================================

// App Router Component
function AppRouter() {
  const { currentPage, navigate } = useNavigation();
  const [showLeadershipAgentModal, setShowLeadershipAgentModal] = useState(false);

  // Render current page
  const renderPage = () => {
    switch (currentPage) {
      case '/analytics':
        return (
          <AnalyticsDashboard 
            theme="light"
            onThemeToggle={() => {}}
            onOpenLeadershipAgent={() => setShowLeadershipAgentModal(true)}
          />
        );
      case '/unified-agent':
        return (
          <>
            <UnifiedAIAgentPage />
          </>
        );
      case '/configuration':
        return (
          <ConfigurationPage />
        );
      case '/pricing':
        // Redirect to external pricing page
        window.location.href = 'https://agent.myora.now/?view=pricepage';
        return null;
      case '/mission':
        return (
          <MissionPage />
        );
      case '/about':
        return (
          <AboutPage />
        );
        // Silently redirect old home page to unified-agent
        navigate('/unified-agent');
        return null;
      default:
        // All unknown routes redirect to unified-agent (NOT home page)
        navigate('/unified-agent');
        return null;
    }
  };

  return <div>
    {renderPage()}
    <AIAgentModal 
      isOpen={showLeadershipAgentModal}
      onClose={() => setShowLeadershipAgentModal(false)}
    />
  </div>;
}

// Main App - Export
export default function App() {
  return (
    <AuthProvider>
      <NavigationProvider>
        <AgentConnectionProvider>
          <AppRouter />
        </AgentConnectionProvider>
      </NavigationProvider>
    </AuthProvider>
  );
}